import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser, LoginResponse } from '@asko/shared';
import { ApiError, api, tokenStore } from './api';
import { clearFlow } from './flow-state';

/** Sotuvchi sessiyasi (BT §3, §6.1). */

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (phone: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Sahifa yangilanganda saqlangan token bo'yicha sessiyani tiklaymiz.
  useEffect(() => {
    const controller = new AbortController();

    if (!tokenStore.get()) {
      setLoading(false);
      return;
    }

    api
      .get<AuthUser>('/auth/me', controller.signal)
      .then((me) => {
        setUser(me);
        setLoading(false);
      })
      .catch((err: unknown) => {
        // So'rov bekor qilindi (komponent qayta o'rnatildi) — bu xato emas.
        // Bu yerda tokenni o'chirish katta xatoga olib kelardi: sahifa
        // yangilanganda sessiya yo'qolib, sotuvchi ishini boshidan
        // boshlashga majbur bo'lardi.
        if (err instanceof DOMException && err.name === 'AbortError') return;

        // Faqat token haqiqatan yaroqsiz bo'lsa o'chiramiz. Tarmoq uzilishida
        // (status 0) token saqlanadi — aloqa tiklangach qayta kirish shart emas.
        if (err instanceof ApiError && err.status === 401) tokenStore.clear();
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const login = useCallback(async (phone: string, password: string) => {
    const result = await api.post<LoginResponse>('/auth/login', { phone, password });
    tokenStore.set(result.accessToken, result.refreshToken);
    setUser(result.user);
  }, []);

  const logout = useCallback(() => {
    tokenStore.clear();
    // Oqim holati ham tozalanadi — aks holda shu vkladkaga kirgan
    // keyingi sotuvchi begona mijozning ishini davom ettirib yuborardi.
    clearFlow();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth faqat AuthProvider ichida ishlatiladi');
  return ctx;
}
